import { getAccessToken } from '../../../../Services/getAccessToken';
import { ENVIRONMENT_URL, RAJPURA_TABLES, getRajpuraTourId, sanitizeParentTourPayload } from '../../../../constants/dataverse';
import { STORES, putToStore, getAllFromStore, getFromStore, deleteFromStore, enqueueSync, getFoodSafetyCheckpointsForTour } from '../../../../Services/offlineDB';

const TABLES = {
    QUALITY_TOUR: RAJPURA_TABLES.QUALITY_TOUR,
    FOOD_SAFETY_CHECKLIST: RAJPURA_TABLES.FOOD_SAFETY
};

const CONFIG_CACHE_KEY = "mrs_bectors_foodsafety_config";

export const foodSafetyService = {
    getConfig: async function (accessToken?: string) {
        // 1. If online and token provided, try fetching latest config from SharePoint
        if (navigator.onLine && accessToken) {
            const listName = "Quality-Rajpura-FoodSafety";
            const query = "$select=Id,Title,Plant,ChecklistType," +
                "QAExecutive/Title,QAExecutive/EMail,QAExecutive/Id," +
                "ProductionIncharge/Title,ProductionIncharge/EMail,ProductionIncharge/Id" +
                "&$expand=QAExecutive,ProductionIncharge";

            try {
                const { buildSharePointListItemsUrl } = await import('../../../../constants/sharepoint');
                const url = buildSharePointListItemsUrl(listName, query);
                const response = await fetch(url, {
                    headers: { "Accept": "application/json;odata=verbose", "Authorization": `Bearer ${accessToken}` }
                });

                if (response.ok) {
                    const data = await response.json();
                    const results = data.d?.results || [];

                    if (results.length > 0) {
                        const parsed = results.map((item: any) => {
                            let qaList: any[] = [];
                            if (item.QAExecutive && item.QAExecutive.results && Array.isArray(item.QAExecutive.results)) {
                                qaList = item.QAExecutive.results.map((u: any) => ({ Title: u.Title, EMail: u.EMail, Id: u.Id }));
                            } else if (item.QAExecutive && item.QAExecutive.Title) {
                                qaList = [{ Title: item.QAExecutive.Title, EMail: item.QAExecutive.EMail, Id: item.QAExecutive.Id }];
                            }

                            let prodList: any[] = [];
                            if (item.ProductionIncharge && item.ProductionIncharge.results && Array.isArray(item.ProductionIncharge.results)) {
                                prodList = item.ProductionIncharge.results.map((u: any) => ({ Title: u.Title, EMail: u.EMail, Id: u.Id }));
                            } else if (item.ProductionIncharge && item.ProductionIncharge.Title) {
                                prodList = [{ Title: item.ProductionIncharge.Title, EMail: item.ProductionIncharge.EMail, Id: item.ProductionIncharge.Id }];
                            }

                            return {
                                Id: item.Id,
                                Title: item.Title,
                                Plant: item.Plant || "Rajpura",
                                ChecklistType: item.ChecklistType || "",
                                QAExecutives: qaList,
                                ProductionIncharges: prodList
                            };
                        });

                        try {
                            localStorage.setItem(CONFIG_CACHE_KEY, JSON.stringify(parsed));
                            await putToStore(STORES.CONFIG, { id: 'foodsafety_config', data: parsed, updatedAt: Date.now() });
                        } catch (cacheErr) {
                            console.warn("Could not cache food safety config:", cacheErr);
                        }

                        return parsed;
                    }
                }
            } catch (e) {
                console.warn("Could not fetch SharePoint config live, checking local cache:", e);
            }
        }

        // 2. Load from IndexedDB config store
        try {
            const idbEntry = await getFromStore(STORES.CONFIG, 'foodsafety_config');
            if (idbEntry && idbEntry.data && Array.isArray(idbEntry.data) && idbEntry.data.length > 0) {
                return idbEntry.data;
            }
        } catch (idbErr) {
            console.warn("Could not read config from IndexedDB:", idbErr);
        }

        // 3. Fallback to localStorage cache
        try {
            const cached = localStorage.getItem(CONFIG_CACHE_KEY);
            if (cached) {
                const parsed = JSON.parse(cached);
                if (Array.isArray(parsed) && parsed.length > 0) {
                    return parsed;
                }
            }
        } catch (storageErr) {
            console.warn("Error reading food safety config cache:", storageErr);
        }

        return [];
    },

    saveSession: async function (sessionData: any) {
        const tourId = getRajpuraTourId(sessionData);
        let method: 'POST' | 'PATCH' = (tourId && !sessionData._isNew) ? 'PATCH' : 'POST';
        const sessionPayload = { ...sessionData };
        delete sessionPayload._isNew;

        const saveOfflineFallback = async () => {
            console.warn("Offline: saving Food Safety session to IndexedDB", sessionPayload);
            const id = tourId || `offline-fs-${Date.now()}`;
            sessionPayload.cr3ea_prod_rajpura_quality_tourid = id;
            sessionPayload.cr3ea_rajpura_quality_tourid = id;
            sessionPayload.cr3ea_prod_qualitytourid = id;
            
            try {
                await putToStore(STORES.FOODSAFETY_SESSIONS, { id, data: sessionPayload, syncStatus: 'pending', updatedAt: Date.now() });
                await enqueueSync({
                    id: `${Date.now()}_session_${id}`,
                    type: 'foodsafety_session',
                    method: method,
                    payload: sessionPayload,
                    createdAt: Date.now()
                });
            } catch (storageErr) {
                console.warn("IndexedDB save failed, continuing in memory:", storageErr);
            }
            return sessionPayload;
        };

        if (!navigator.onLine) {
            return await saveOfflineFallback();
        }

        try {
            const tokenResult = await getAccessToken(undefined, undefined, true);
            const accessToken = tokenResult?.token;
            if (!accessToken) {
                return await saveOfflineFallback();
            }

            const tableCandidates = [
                RAJPURA_TABLES.QUALITY_TOUR
            ];
            const apiVersion = "9.2";
            let response: Response | null = null;
            let lastErrText = "";

            for (const tableName of tableCandidates) {
                const currentPayload = sanitizeParentTourPayload(sessionPayload);
                let url = `${ENVIRONMENT_URL}/api/data/v${apiVersion}/${tableName}`;

                if (method === "PATCH" && tourId) {
                    url += `(${tourId})`;
                }

                delete currentPayload.cr3ea_prod_rajpura_quality_tourid;
                delete currentPayload.cr3ea_rajpura_quality_tourid;
                delete currentPayload.cr3ea_prod_qualitytourid;
                delete currentPayload.cr3ea_qualitytourid;
                delete currentPayload.id;

                try {
                    const res = await fetch(url, {
                        method: method,
                        headers: {
                            "Accept": "application/json",
                            "Content-Type": "application/json; charset=utf-8",
                            "OData-MaxVersion": "4.0",
                            "OData-Version": "4.0",
                            "Prefer": "return=representation",
                            "Authorization": `Bearer ${accessToken}`
                        },
                        body: JSON.stringify(currentPayload)
                    });
                    if (res.ok) {
                        response = res;
                        break;
                    } else {
                        lastErrText = await res.text();
                        console.warn(`Dataverse FS session save to '${tableName}' failed (${res.status}):`, lastErrText);
                    }
                } catch (e) {
                    lastErrText = String(e);
                }
            }

            if (!response || !response.ok) {
                console.error(`Dataverse session save failed: ${lastErrText}`);
                return await saveOfflineFallback();
            }

            const resultData = method === "PATCH" ? sessionData : await response.json();
            const finalId = getRajpuraTourId(resultData) || tourId;
            if (finalId) {
                resultData.cr3ea_prod_rajpura_quality_tourid = finalId;
                resultData.cr3ea_rajpura_quality_tourid = finalId;
                resultData.cr3ea_prod_qualitytourid = finalId;
                try {
                    await putToStore(STORES.FOODSAFETY_SESSIONS, { id: finalId, data: resultData, syncStatus: 'synced', updatedAt: Date.now() });
                } catch (e) {
                    console.warn("Cache put failed", e);
                }
            }

            return resultData;
        } catch (netErr) {
            console.warn("Network error during session save, falling back to offline:", netErr);
            return await saveOfflineFallback();
        }
    },

    saveChecklistRow: async function (rowRecord: any) {
        const payload = { ...rowRecord };
        delete payload.id;

        const rowIdVal = payload.cr3ea_prod_foodsafetychecklistforrajpuraid || payload.cr953_foodsafetychecklistforrajpuraid;
        let method: 'POST' | 'PATCH' = rowIdVal ? 'PATCH' : 'POST';

        const saveOfflineRowFallback = async () => {
            console.warn("Offline: saving checklist row to IndexedDB", payload);
            const id = rowIdVal || `offline-row-${Date.now()}-${Math.random()}`;
            payload.cr3ea_prod_foodsafetychecklistforrajpuraid = id;
            payload.cr953_foodsafetychecklistforrajpuraid = id;
            
            const bindStr = payload['cr3ea_qualitytourid@odata.bind'] || payload['cr953_food_safety_tourid@odata.bind'] || '';
            const tourIdMatch = bindStr ? bindStr.match(/\(([^)]+)\)/) : null;
            const tourId = tourIdMatch ? tourIdMatch[1] : '';
            
            try {
                await putToStore(STORES.FOODSAFETY_CHECKPOINTS, { id, tourId: tourId, data: payload, syncStatus: 'pending' });
                await enqueueSync({
                    id: `${Date.now()}_chk_${id}`,
                    type: 'foodsafety_checkpoint',
                    method: method,
                    payload: payload,
                    createdAt: Date.now()
                });
            } catch (storageErr) {
                console.warn("Checkpoint local store failed:", storageErr);
            }
            return payload;
        };

        if (!navigator.onLine) {
            return await saveOfflineRowFallback();
        }

        try {
            const tokenResult = await getAccessToken(undefined, undefined, true);
            const accessToken = tokenResult?.token;
            if (!accessToken) {
                return await saveOfflineRowFallback();
            }

            const apiVersion = "9.2";
            const tableCandidates = [TABLES.FOOD_SAFETY_CHECKLIST];

            let response: Response | null = null;
            let lastErrorText = "";

            for (const tableName of tableCandidates) {
                const currentPayload = { ...payload };
                let url = `${ENVIRONMENT_URL}/api/data/v${apiVersion}/${tableName}`;
                if (method === "PATCH" && rowIdVal) {
                    url += `(${rowIdVal})`;
                    delete currentPayload.cr3ea_prod_foodsafetychecklistforrajpuraid;
                    delete currentPayload.cr953_foodsafetychecklistforrajpuraid;
                }

                try {
                    const res = await fetch(url, {
                        method: method,
                        headers: {
                            "Accept": "application/json",
                            "Content-Type": "application/json; charset=utf-8",
                            "OData-MaxVersion": "4.0",
                            "OData-Version": "4.0",
                            "Prefer": "return=representation",
                            "Authorization": `Bearer ${accessToken}`
                        },
                        body: JSON.stringify(currentPayload)
                    });
                    if (res.ok) {
                        response = res;
                        break;
                    } else {
                        lastErrorText = await res.text();
                    }
                } catch (err) {
                    lastErrorText = String(err);
                }
            }

            if (!response || !response.ok) {
                console.error(`Dataverse checklist row save failed: ${lastErrorText}`);
                return await saveOfflineRowFallback();
            }

            const resultData = method === "PATCH" ? payload : await response.json();
            const bindStr = payload['cr3ea_qualitytourid@odata.bind'] || payload['cr953_food_safety_tourid@odata.bind'] || '';
            const tourIdMatch = bindStr ? bindStr.match(/\(([^)]+)\)/) : null;
            const tourId = tourIdMatch ? tourIdMatch[1] : '';
            const rowId = resultData.cr3ea_prod_foodsafetychecklistforrajpuraid || resultData.cr953_foodsafetychecklistforrajpuraid || rowIdVal;
            if (rowId) {
                try {
                    await putToStore(STORES.FOODSAFETY_CHECKPOINTS, { id: rowId, tourId: tourId, data: resultData, syncStatus: 'synced' });
                } catch (e) {
                    console.warn("Checkpoint cache put failed", e);
                }
            }
            return resultData;
        } catch (netErr) {
            console.warn("Network error saving checklist row, saving locally:", netErr);
            return await saveOfflineRowFallback();
        }
    },

    getTourHistory: async function () {
        let allTours: any[] = [];
        let dvTours: any[] = [];

        // 1. Fetch live from Dataverse first if online
        if (navigator.onLine) {
            try {
                const tokenResult = await getAccessToken(undefined, undefined, true);
                const accessToken = tokenResult?.token;
                if (accessToken) {
                    const tableName = TABLES.QUALITY_TOUR;
                    const apiVersion = "9.2";
                    const url = `${ENVIRONMENT_URL}/api/data/v${apiVersion}/${tableName}?$filter=(cr3ea_plantid eq '14' or cr3ea_plantid eq '2' or cr3ea_plantid eq 'Rajpura')&$orderby=cr3ea_tourstartdate desc&$top=100`;

                    const response = await fetch(url, {
                        method: "GET",
                        headers: {
                            "Accept": "application/json",
                            "OData-MaxVersion": "4.0",
                            "OData-Version": "4.0",
                            "Authorization": `Bearer ${accessToken}`
                        }
                    });

                    if (response.ok) {
                        const data = await response.json();
                        dvTours = data.value || [];
                        for (const dvTour of dvTours) {
                            const dvId = String(dvTour.cr3ea_prod_rajpura_quality_tourid || dvTour.cr3ea_prod_qualitytourid || "").toLowerCase();
                            if (dvId) {
                                try {
                                    await putToStore(STORES.FOODSAFETY_SESSIONS, {
                                        id: dvId,
                                        data: dvTour,
                                        syncStatus: 'synced',
                                        updatedAt: Date.now()
                                    });
                                } catch (_) {}
                            }
                        }
                    }
                }
            } catch (err) {
                console.warn("Could not fetch remote tour history:", err);
            }
        }

        if (dvTours.length > 0) {
            allTours = [...dvTours];
        }

        // 2. Merge local pending / draft items from IndexedDB
        try {
            const sessions = await getAllFromStore(STORES.FOODSAFETY_SESSIONS);
            if (sessions && sessions.length > 0) {
                for (const s of sessions) {
                    const d = s.data || s;
                    const sId = String(d.cr3ea_prod_rajpura_quality_tourid || d.cr3ea_prod_qualitytourid || "").toLowerCase();
                    if (sId && !allTours.some(t => String(t.cr3ea_prod_rajpura_quality_tourid || t.cr3ea_prod_qualitytourid || "").toLowerCase() === sId)) {
                        allTours.push(d);
                    }
                }
            }
        } catch (dbErr) {
            console.warn("Could not read local DB sessions:", dbErr);
        }

        // 3. Fallback to localStorage cache
        if (allTours.length === 0) {
            try {
                const cached = localStorage.getItem("mrs_bectors_foodsafety_tours_cache");
                if (cached) {
                    const parsed = JSON.parse(cached);
                    if (Array.isArray(parsed) && parsed.length > 0) {
                        allTours = parsed;
                    }
                }
            } catch (_) {}
        } else {
            try {
                localStorage.setItem("mrs_bectors_foodsafety_tours_cache", JSON.stringify(allTours));
            } catch (_) {}
        }

        // 4. Fallback seed items only if completely empty on first launch
        // if (allTours.length === 0) {
        //     allTours = [
        //         {
        //             cr3ea_prod_rajpura_quality_tourid: "fs-tour-seed-1",
        //             cr3ea_food_safety_checklisttype: "PPE Checklist",
        //             cr3ea_title: "FoodSafety_PPE_Rajpura_Line 1_17082026",
        //             cr3ea_plantid: "Rajpura",
        //             cr3ea_lineno: "Line 1",
        //             cr3ea_assigned_qa: "Mishab Muhammed",
        //             cr3ea_shiftexecutiveproduction: "Shaan Arshaqu",
        //             cr3ea_shift: "Shift 1",
        //             cr3ea_food_safety_cycle: "Cycle-1",
        //             cr3ea_tourstartdate: new Date(Date.now() - 3600000 * 3).toISOString(),
        //             cr3ea_tourcompletiondate: new Date(Date.now() - 3600000 * 2).toISOString(),
        //             cr3ea_status: "Submitted",
        //             cr3ea_checklist_result: "Pass",
        //             cr3ea_overall_score: "96.00%",
        //             cr3ea_food_safety_area: "Packing"
        //         },
        //         {
        //             cr3ea_prod_rajpura_quality_tourid: "fs-tour-seed-2",
        //             cr3ea_food_safety_checklisttype: "GMP Checklist",
        //             cr3ea_title: "FoodSafety_GMP_Rajpura_Line 2_16082026",
        //             cr3ea_plantid: "Rajpura",
        //             cr3ea_lineno: "Line 2",
        //             cr3ea_assigned_qa: "Gokul K",
        //             cr3ea_shiftexecutiveproduction: "Ajith K",
        //             cr3ea_shift: "Shift 2",
        //             cr3ea_food_safety_cycle: "Cycle-2",
        //             cr3ea_tourstartdate: new Date(Date.now() - 86400000).toISOString(),
        //             cr3ea_tourcompletiondate: new Date(Date.now() - 86400000 + 1800000).toISOString(),
        //             cr3ea_status: "Submitted",
        //             cr3ea_checklist_result: "Pass",
        //             cr3ea_overall_score: "91.30%"
        //         },
        //         {
        //             cr3ea_prod_rajpura_quality_tourid: "fs-tour-seed-3",
        //             cr3ea_food_safety_checklisttype: "PCI Checklist",
        //             cr3ea_title: "FoodSafety_PCI_Rajpura_Line 1_15082026",
        //             cr3ea_plantid: "Rajpura",
        //             cr3ea_lineno: "Line 1",
        //             cr3ea_assigned_qa: "Mishab Muhammed",
        //             cr3ea_shiftexecutiveproduction: "Mishab Muhammed",
        //             cr3ea_shift: "Shift 1",
        //             cr3ea_food_safety_cycle: "Cycle-1",
        //             cr3ea_tourstartdate: new Date(Date.now() - 86400000 * 2).toISOString(),
        //             cr3ea_tourcompletiondate: new Date(Date.now() - 86400000 * 2 + 2400000).toISOString(),
        //             cr3ea_status: "Submitted",
        //             cr3ea_checklist_result: "Pass",
        //             cr3ea_overall_score: "100.00%",
        //             cr3ea_food_safety_area: "Old Block"
        //         }
        //     ];
        // }

        // 4. Normalize fields across all items
        return allTours.map(t => {
            let chkType = t.cr3ea_food_safety_checklisttype;
            if (!chkType && t.cr3ea_title) {
                const title = String(t.cr3ea_title);
                if (title.includes("PPE")) chkType = "PPE Checklist";
                else if (title.includes("GMP")) chkType = "GMP Checklist";
                else if (title.includes("PCI")) chkType = "PCI Checklist";
            }
            if (!chkType) chkType = "PPE Checklist";

            return {
                ...t,
                cr3ea_food_safety_checklisttype: chkType,
                cr3ea_plantid: t.cr3ea_plantid || "Rajpura",
                cr3ea_lineno: t.cr3ea_lineno || t.cr3ea_lineid || "Line 1",
                cr3ea_overall_score: t.cr3ea_overall_score || "100%",
                cr3ea_checklist_result: t.cr3ea_checklist_result || "Pass",
                cr3ea_status: t.cr3ea_status || t.cr3ea_processstatus || "In Progress"
            };
        });
    },

    getChecklistItems: async function (tourId: string) {
        if (!navigator.onLine) {
            console.warn("Offline: loading checklist rows from local DB");
            return await getFoodSafetyCheckpointsForTour(tourId);
        }

        try {
            const tokenResult = await getAccessToken(undefined, undefined, true);
            const accessToken = tokenResult?.token;
            if (!accessToken) {
                return await getFoodSafetyCheckpointsForTour(tourId);
            }

            const apiVersion = "9.2";
            const cleanTourId = tourId ? String(tourId).replace(/[{}]/g, "").trim().toLowerCase() : "";

            const tableCandidates = [
                RAJPURA_TABLES.FOOD_SAFETY
            ];

            let items: any[] = [];
            for (const tableName of tableCandidates) {
                const filters = [
                    `?$filter=_cr3ea_qualitytourid_value eq '${cleanTourId}'`,
                    `?$filter=_cr953_food_safety_tourid_value eq '${cleanTourId}'`
                ];
                for (const filter of filters) {
                    try {
                        const url = `${ENVIRONMENT_URL}/api/data/v${apiVersion}/${tableName}${filter}`;
                        const response = await fetch(url, {
                            method: "GET",
                            headers: {
                                "Accept": "application/json",
                                "OData-MaxVersion": "4.0",
                                "OData-Version": "4.0",
                                "Authorization": `Bearer ${accessToken}`
                            }
                        });
                        if (response.ok) {
                            const data = await response.json();
                            if (data.value && data.value.length > 0) {
                                items = data.value;
                                break;
                            }
                        }
                    } catch (_) {}
                }
                if (items.length > 0) break;
            }

            if (items.length > 0) {
                for (const it of items) {
                    const rowId = it.cr3ea_prod_foodsafetychecklistforrajpuraid || it.cr953_foodsafetychecklistforrajpuraid;
                    if (rowId) {
                        try {
                            await putToStore(STORES.FOODSAFETY_CHECKPOINTS, {
                                id: rowId,
                                tourId: cleanTourId,
                                data: it,
                                syncStatus: 'synced'
                            });
                        } catch (e) {
                            console.warn("Cache put failed for item:", e);
                        }
                    }
                }
                return items;
            }

            return await getFoodSafetyCheckpointsForTour(tourId);
        } catch (netErr) {
            console.warn("Network error fetching checklist items, loading from offline store:", netErr);
            return await getFoodSafetyCheckpointsForTour(tourId);
        }
    },

    syncOfflineData: async function () {
        if (!navigator.onLine) {
            console.log("Device is offline. Skipping sync.");
            return { syncedCount: 0, failedCount: 0 };
        }

        try {
            const queue = await getAllFromStore(STORES.SYNC_QUEUE);
            const fsItems = queue.filter(item => item.type === 'foodsafety_session' || item.type === 'foodsafety_checkpoint');

            if (fsItems.length === 0) {
                return { syncedCount: 0, failedCount: 0 };
            }

            const tokenResult = await getAccessToken(undefined, undefined, true);
            const accessToken = tokenResult?.token;
            if (!accessToken) {
                console.warn("Sync skipped: No access token available.");
                return { syncedCount: 0, failedCount: fsItems.length };
            }

            let syncedCount = 0;
            let failedCount = 0;

            for (const item of fsItems) {
                try {
                    const tableName = item.type === 'foodsafety_session' ? TABLES.QUALITY_TOUR : TABLES.FOOD_SAFETY_CHECKLIST;
                    const apiVersion = "9.2";
                    let url = `${ENVIRONMENT_URL}/api/data/v${apiVersion}/${tableName}`;
                    const payload = { ...item.payload };

                    const isSession = item.type === 'foodsafety_session';
                    const oldOfflineId = isSession 
                        ? (payload.cr3ea_prod_rajpura_quality_tourid || payload.cr3ea_prod_qualitytourid)
                        : payload.cr953_foodsafetychecklistforrajpuraid;
                    const isNewOfflineRecord = oldOfflineId && String(oldOfflineId).startsWith('offline-');

                    if (item.method === 'PATCH' && !isNewOfflineRecord) {
                        if (oldOfflineId) url += `(${oldOfflineId})`;
                        delete payload.cr3ea_prod_rajpura_quality_tourid;
                        delete payload.cr3ea_prod_qualitytourid;
                        delete payload.cr953_foodsafetychecklistforrajpuraid;
                    } else {
                        // POST for new record: strip temporary offline keys so Dataverse generates real GUID
                        if (isNewOfflineRecord) {
                            delete payload.cr3ea_prod_rajpura_quality_tourid;
                            delete payload.cr3ea_prod_qualitytourid;
                            delete payload.cr953_foodsafetychecklistforrajpuraid;
                        }
                    }
                    delete payload._isNew;

                    const response = await fetch(url, {
                        method: (item.method === 'PATCH' && !isNewOfflineRecord) ? 'PATCH' : 'POST',
                        headers: {
                            "Accept": "application/json",
                            "Content-Type": "application/json; charset=utf-8",
                            "OData-MaxVersion": "4.0",
                            "OData-Version": "4.0",
                            "Prefer": "return=representation",
                            "Authorization": `Bearer ${accessToken}`
                        },
                        body: JSON.stringify(payload)
                    });

                    if (response.ok) {
                        let resData: any = {};
                        try {
                            resData = await response.json();
                        } catch (_) {}

                        const newId = resData.cr3ea_prod_rajpura_quality_tourid || resData.cr3ea_prod_qualitytourid || oldOfflineId;

                        if (isSession && newId) {
                            if (oldOfflineId && oldOfflineId !== newId) {
                                await deleteFromStore(STORES.FOODSAFETY_SESSIONS, oldOfflineId);
                            }
                            await putToStore(STORES.FOODSAFETY_SESSIONS, {
                                id: newId,
                                data: {
                                    ...item.payload,
                                    ...resData,
                                    cr3ea_prod_rajpura_quality_tourid: newId,
                                    cr3ea_prod_qualitytourid: newId,
                                    cr3ea_status: item.payload.cr3ea_status || "In Progress"
                                },
                                syncStatus: 'synced',
                                updatedAt: Date.now()
                            });
                        }

                        await deleteFromStore(STORES.SYNC_QUEUE, item.id);
                        syncedCount++;
                    } else {
                        const errTxt = await response.text();
                        console.error(`Sync failed for item ${item.id}: ${response.status} - ${errTxt}`);
                        failedCount++;
                    }
                } catch (e) {
                    console.error("Failed to sync item:", item.id, e);
                    failedCount++;
                }
            }

            console.log(`Food Safety offline sync complete: ${syncedCount} synced, ${failedCount} failed.`);
            return { syncedCount, failedCount };
        } catch (err) {
            console.error("Error running Food Safety offline sync:", err);
            return { syncedCount: 0, failedCount: 0 };
        }
    },

    deleteChecklistItem: async function (guid: string) {
        const tokenResult = await getAccessToken(undefined, undefined, true);
        const accessToken = tokenResult?.token;
        if (!accessToken) throw new Error("Access token is missing");

        const tableName = TABLES.FOOD_SAFETY_CHECKLIST;
        const apiVersion = "9.2";

        const url = `${ENVIRONMENT_URL}/api/data/v${apiVersion}/${tableName}(${guid})`;

        const response = await fetch(url, {
            method: "DELETE",
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json; charset=utf-8",
                "OData-MaxVersion": "4.0",
                "OData-Version": "4.0",
                "Authorization": `Bearer ${accessToken}`
            }
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Dataverse delete checklist item failed: ${response.status} - ${errorText}`);
        }
        return true;
    },

    cleanChecklistItems: async function (tourId: string) {
        if (!navigator.onLine) {
            console.warn("Offline mode: Skipping cleanChecklistItems. Will be handled during sync if needed.");
            return;
        }

        try {
            const existing = await this.getChecklistItems(tourId);
            if (existing && existing.length > 0) {
                console.log(`Cleaning up ${existing.length} obsolete child records for Tour ID: ${tourId}`);
                for (const item of existing) {
                    if (item.cr953_foodsafetychecklistforrajpuraid) {
                        await this.deleteChecklistItem(item.cr953_foodsafetychecklistforrajpuraid);
                    }
                }
            }
        } catch (e) {
            console.warn("Failed to clean up old checklist items, proceeding with save:", e);
        }
    }
};
